import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { MapContainer, TileLayer, CircleMarker, Popup } from 'react-leaflet';
import { ClipboardList, Mic, ChevronDown, ChevronRight, MapPin } from 'lucide-react';
import { getSurveyAnalytics, SurveyProjectAnalytics } from '../../services/annotationApi';
import { getSessions } from '../../services/api';
import { formatHm } from '../../utils/formatDuration';
import { UserEmail } from '../../components/common/UserEmail';

type DateMode = 'all' | '24h' | '7d' | '30d';

const PRESETS: { key: DateMode; label: string }[] = [
  { key: '24h', label: 'Last 24h' },
  { key: '7d', label: 'Last 7 days' },
  { key: '30d', label: 'Last 30 days' },
  { key: 'all', label: 'All time' },
];

function presetRange(mode: DateMode): { startDate?: string; endDate?: string } {
  const now = Date.now();
  switch (mode) {
    case '24h':
      return { startDate: new Date(now - 24 * 3600 * 1000).toISOString() };
    case '7d':
      return { startDate: new Date(now - 7 * 24 * 3600 * 1000).toISOString() };
    case '30d':
      return { startDate: new Date(now - 30 * 24 * 3600 * 1000).toISOString() };
    default:
      return {};
  }
}

interface MapSession {
  sessionId: string;
  language: string;
  status: string;
  projectId?: string;
  location?: { state?: string; district?: string; village?: string; gps?: { lat: number; lng: number } };
  enumeratorName?: string;
  supervisorName?: string;
}

export default function SurveyAnalyticsPage() {
  const [mode, setMode] = useState<DateMode>('all');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [mapProjectId, setMapProjectId] = useState<string>('all');

  const range = useMemo(() => presetRange(mode), [mode]);

  const { data: projects = [], isLoading } = useQuery<SurveyProjectAnalytics[]>({
    queryKey: ['survey-analytics', mode],
    queryFn: () => getSurveyAnalytics(range),
  });

  // Map points — all GPS sessions (projectId now projected), filtered to survey
  // projects (or the selected one). Fetched once, independent of the window.
  const { data: mapData } = useQuery({
    queryKey: ['survey-map'],
    queryFn: () => getSessions({ gpsOnly: 'true' }),
  });

  const surveyProjectIds = useMemo(() => new Set(projects.map((p) => p.projectId)), [projects]);

  const mapSessions: MapSession[] = useMemo(() => {
    const all = (mapData?.sessions ?? []) as MapSession[];
    return all.filter((s) => {
      if (!s.location?.gps?.lat || !s.location?.gps?.lng) return false;
      if (!s.projectId) return false;
      if (mapProjectId === 'all') return surveyProjectIds.has(s.projectId);
      return s.projectId === mapProjectId;
    });
  }, [mapData, mapProjectId, surveyProjectIds]);

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const statusColor: Record<string, string> = {
    completed: '#10b981',
    uploading: '#3b82f6',
    failed: '#ef4444',
    draft: '#9ca3af',
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <ClipboardList size={22} className="text-violet-600" /> Survey Analytics
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Uploaded volume per supervisor for each survey project.
          </p>
        </div>
        <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
          {PRESETS.map((p) => (
            <button
              key={p.key}
              onClick={() => setMode(p.key)}
              className={`px-3 py-1.5 text-sm rounded-md transition ${
                mode === p.key ? 'bg-white shadow text-violet-700 font-medium' : 'text-gray-600'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <div className="text-center py-12 text-gray-400">Loading…</div>
      ) : projects.length === 0 ? (
        <div className="card text-center py-12 text-gray-500">
          No survey projects yet. Mark a project as a Survey Project to see it here.
        </div>
      ) : (
        <div className="space-y-3">
          {projects.map((proj) => {
            const open = expanded.has(proj.projectId);
            return (
              <div key={proj.projectId} className="card p-0 overflow-hidden">
                <button
                  onClick={() => toggle(proj.projectId)}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 text-left"
                >
                  {open ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-gray-900 truncate">{proj.name}</span>
                      {proj.voiceOnly && (
                        <span className="badge badge-purple inline-flex items-center gap-1">
                          <Mic size={11} /> Voice
                        </span>
                      )}
                      {proj.status !== 'active' && (
                        <span className="badge badge-gray">{proj.status}</span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {proj.totalSessions} recordings · {formatHm(proj.totalSeconds)} ·{' '}
                      {proj.supervisors.length} supervisor{proj.supervisors.length === 1 ? '' : 's'}
                    </div>
                  </div>
                  <span
                    className="text-xs text-violet-600 hover:text-violet-800 flex items-center gap-1"
                    onClick={(e) => {
                      e.stopPropagation();
                      setMapProjectId(proj.projectId);
                      document.getElementById('survey-map')?.scrollIntoView({ behavior: 'smooth' });
                    }}
                  >
                    <MapPin size={13} /> Map
                  </span>
                </button>

                {open && (
                  <div className="border-t border-gray-100">
                    {proj.supervisors.length === 0 ? (
                      <div className="px-4 py-4 text-sm text-gray-400">
                        No uploads in this window.
                      </div>
                    ) : (
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-xs text-gray-500 uppercase tracking-wider">
                            <th className="px-4 py-2 text-left font-semibold">Supervisor</th>
                            <th className="px-4 py-2 text-right font-semibold">Recordings</th>
                            <th className="px-4 py-2 text-right font-semibold">Volume</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                          {proj.supervisors.map((s) => (
                            <tr key={s.supervisorId} className="hover:bg-gray-50">
                              <td className="px-4 py-2">
                                <div className="font-medium text-gray-800">{s.supervisorName}</div>
                                {s.supervisorEmail && (
                                  <UserEmail email={s.supervisorEmail} />
                                )}
                              </td>
                              <td className="px-4 py-2 text-right tabular-nums">{s.sessions}</td>
                              <td className="px-4 py-2 text-right tabular-nums">
                                {formatHm(s.seconds)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Map */}
      <div id="survey-map" className="space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <MapPin size={18} className="text-violet-600" /> Survey Map
          </h2>
          <select
            className="input w-64"
            value={mapProjectId}
            onChange={(e) => setMapProjectId(e.target.value)}
          >
            <option value="all">All survey projects</option>
            {projects.map((p) => (
              <option key={p.projectId} value={p.projectId}>
                {p.name}
              </option>
            ))}
          </select>
        </div>
        <p className="text-gray-500 text-xs">{mapSessions.length} recordings with GPS</p>
        <div className="h-[500px] rounded-xl overflow-hidden border border-gray-200 shadow-sm">
          <MapContainer
            center={[20.5937, 78.9629]}
            zoom={5}
            style={{ height: '100%', width: '100%' }}
            preferCanvas
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {mapSessions.map((s) => (
              <CircleMarker
                key={s.sessionId}
                center={[s.location!.gps!.lat, s.location!.gps!.lng]}
                radius={6}
                pathOptions={{
                  fillColor: statusColor[s.status] ?? '#9ca3af',
                  fillOpacity: 0.8,
                  color: '#fff',
                  weight: 1.5,
                }}
              >
                <Popup>
                  <div className="text-sm space-y-1 min-w-[180px]">
                    <p className="font-semibold">{s.language || '—'}</p>
                    <p className="text-gray-600">
                      {s.location?.village}, {s.location?.district}
                    </p>
                    <p className="text-gray-600">{s.location?.state}</p>
                    <p className="text-gray-500 text-xs">Supervisor: {s.supervisorName || '—'}</p>
                  </div>
                </Popup>
              </CircleMarker>
            ))}
          </MapContainer>
        </div>
      </div>
    </div>
  );
}
